import { useState } from "react";
import { api, ApiError, guardarToken, type Colaborador } from "../api";

/**
 * ENTRADA NO SISTEMA COLABORADORES — CPF e senha.
 *
 * CPF, e não usuário: quem bate ponto é o quadro inteiro, e a maioria não tem
 * conta em sistema nenhum. CPF é o que a pessoa sabe de cor, sem precisar
 * lembrar de um login que alguém inventou para ela.
 *
 * `inputMode="numeric"` abre o teclado numérico no celular — quem digita CPF
 * numa tecladinha de letras erra, e errar aqui é ficar sem bater.
 */

interface Props {
  onEntrou: (colaborador: Colaborador) => void;
  /** Por que a entrada pelo Portal não deu certo. */
  aviso?: string | null;
}

/** 12345678901 -> 123.456.789-01, enquanto se digita. */
function formatarCpf(v: string): string {
  const d = v.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

export default function Login({ onEntrou, aviso }: Props) {
  const [cpf, setCpf] = useState("");
  const [senha, setSenha] = useState("");
  const [erro, setErro] = useState<string | null>(aviso ?? null);
  const [entrando, setEntrando] = useState(false);

  async function entrar(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    setEntrando(true);
    try {
      const r = await api.login(cpf.replace(/\D/g, ""), senha);
      guardarToken(r.token);
      onEntrou({ ...r.colaborador, precisa_trocar: r.precisa_trocar });
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não foi possível entrar. Tente de novo.");
    } finally {
      setEntrando(false);
    }
  }

  return (
    <div className="login-tela">
      <form className="login-caixa" onSubmit={entrar}>
        <div className="login-marca">
          <span className="login-relogio" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="34" height="34" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <rect x="3" y="5" width="18" height="14" rx="2.5" />
              <circle cx="9" cy="11" r="2.2" />
              <path d="M5.8 16c.6-1.6 1.8-2.4 3.2-2.4s2.6.8 3.2 2.4M14.5 10h4M14.5 13.5h3" />
            </svg>
          </span>
          <h1>Colaboradores SERCAMP</h1>
          <p>Seu ponto, suas folhas, seus documentos</p>
        </div>

        <label className="campo">
          <span>CPF</span>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="username"
            placeholder="000.000.000-00"
            value={cpf}
            onChange={(e) => setCpf(formatarCpf(e.target.value))}
            required
          />
        </label>

        <label className="campo">
          <span>Senha</span>
          <input
            type="password"
            autoComplete="current-password"
            placeholder="Sua senha"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            required
          />
        </label>

        {erro && <div className="aviso-erro">{erro}</div>}

        <button className="botao-principal" type="submit" disabled={entrando}>
          {entrando ? "Entrando…" : "Entrar"}
        </button>

        <p className="login-ajuda">
          Primeiro acesso ou esqueceu a senha? Procure o <strong>Departamento Pessoal</strong>.
        </p>
      </form>
    </div>
  );
}
